import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import i18n from 'i18n';
import { cgToast, notifyError } from 'modules/Common/ui/toast';
import { trackFreeSupplierPortalPublished } from 'helpers/mixpanel';
import { useBranding } from '../App/hooks/useBranding';
import { normalizeHexColor } from '../Common/utils/brandColor';
import {
  fetchSupplierPublicationSettings,
  regenerateSupplierPublicationSlug,
  updateSupplierPublicationSettings,
} from './supplierPublicationApi';

// React-query wiring for the S8 Supplier Page/widget publication management
// contract (settings query + update mutation + regenerate-slug mutation).
// Replaces the legacy /operator/cbam-portal/* wiring (useCbamPortalStatus /
// useConfigureCbamPortal / useDisableCbamPortal in app/state/liteHooks.js) —
// that wiring has been removed from the supplier surface entirely (D-S9-3).

export const SUPPLIER_PUBLICATION_QUERY_KEY = ['supplierPublication'];

export const useSupplierPublicationSettings = ({ enabled = true } = {}) =>
  useQuery({
    queryKey: SUPPLIER_PUBLICATION_QUERY_KEY,
    queryFn: fetchSupplierPublicationSettings,
    staleTime: 0,
    enabled,
  });

export const useUpdateSupplierPublication = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateSupplierPublicationSettings,
    onSuccess: async (settings) => {
      // "Going live" is a disabled→enabled TRANSITION, not any save while already
      // enabled — otherwise ordinary settings edits (profile, slug, and the color
      // picker's frequent saves, which all echo enabled:true) would each fire the
      // publish toast and inflate publication analytics. Compare against the
      // previously-cached state before overwriting it.
      const previous = queryClient.getQueryData(SUPPLIER_PUBLICATION_QUERY_KEY);
      const justPublished = !previous?.enabled && settings.enabled;
      queryClient.setQueryData(SUPPLIER_PUBLICATION_QUERY_KEY, settings);
      cgToast.success('Saved', {
        description: justPublished
          ? 'Your CBAM Portal is live'
          : 'Your CBAM Portal settings were saved',
      });
      if (justPublished) trackFreeSupplierPortalPublished();
      await queryClient.invalidateQueries({ queryKey: SUPPLIER_PUBLICATION_QUERY_KEY });
    },
    onError: notifyError,
  });
};

// The persisted supplier brand color (D50), shared by the Embed Widget and
// Share & Portal pickers so both edit one value identically. Resolves the
// effective color (supplier's saved color → deployment/whitelabel brand → CG
// green) and persists a new one via a MINIMAL full-echo PUT: the strict request
// needs version + enabled (+ includeFutureRevisions while enabled), and the
// backend leaves every omitted field unchanged, so this moves only the color.
export const useSupplierBrandColor = () => {
  const { data: settings } = useSupplierPublicationSettings();
  const updateMutation = useUpdateSupplierPublication();
  const brand = useBranding();
  const effectiveColor =
    normalizeHexColor(settings?.primaryColor) || normalizeHexColor(brand.primaryColor) || '#00b894';
  const persistColor = (hex) => {
    if (!settings) return;
    updateMutation.mutate({
      version: 1,
      enabled: settings.enabled,
      includeFutureRevisions: settings.includeFutureRevisions,
      primaryColor: hex,
    });
  };
  return { effectiveColor, persistColor, saving: updateMutation.isPending };
};

export const useRegenerateSupplierPublicationSlug = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: regenerateSupplierPublicationSlug,
    onSuccess: async (settings) => {
      queryClient.setQueryData(SUPPLIER_PUBLICATION_QUERY_KEY, settings);
      cgToast.success(i18n.t('freeSupplier:toasts.linkRegeneratedTitle', 'Link regenerated'), {
        description: i18n.t(
          'freeSupplier:toasts.linkRegeneratedBody',
          'The previous link stopped working immediately',
        ),
      });
      await queryClient.invalidateQueries({ queryKey: SUPPLIER_PUBLICATION_QUERY_KEY });
    },
    onError: notifyError,
  });
};
